import { upperFirst } from "lodash";
import firebase from "firebase/app";
import { CSSProperties, FC, useEffect, useMemo, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";

import { ComboBox, NumberInput } from "..";
import { PdModal, PdModalCloseButton } from "../overlay/modal";
import { SLImage } from "../ReactiveImage";
import { tOption } from "../ComboBox";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { tModalComponentProps } from "src/redux/modals/types";
import { formatMoney } from "src/util/gFormatting";
import { bound } from "src/util/functions";
import {
  findVariantByOptionList,
  getVariantOptions,
  getVariantOptionsList,
  isVariantAvailableByOptionValue,
} from "src/util/product";
import { isApiError } from "src/util/apiNew";
import { kRoute } from "src/kRoute";
import { navigationRef } from "src/RootNavigation";
import { CheckMark2 } from "../NewCheckBox2";
import { tAvailability, tImage, tProduct, tVariant, validate } from "tmt_api/src/types/types.validator";
import { useAppDispatch } from "src/redux";
import { addProductToCart } from "src/redux/user/actions";

type tVariantItem = {
  name: string;
  index: number;
  size: number;
  options: string[];
  value?: string;
  onSelect?: (option: tOption<string>, variantIndex: number) => void;
  onSelectUnavailable?: (option: tOption<string>, myIndex: number) => void;
  filterAvailable?: (option: tOption<string>, variantIndex: number) => boolean;
};

export type tProductModal = {
  name: string;
  brandUid: string;
  brandName: string;
  tester?: {
    price: number;
    sku: string;
  };
  images?: tImage[];
  variants: tVariant[];
  availability: tAvailability;
  sku: string;
  wholesalePrice: number;
  msrpPrice: number;
  caseMinimum: number;
  productId: string;
  inventory: number;
};

export type tAddProductModal = {
  action: "add" | "edit";
  productUid?: string;
  product?: tProductModal;
};

type tModalContent = Pick<tModalComponentProps<tAddProductModal>, "onClose" | "onReject" | "onResolve">;

const getProductById = async (id?: string): Promise<tProduct> => {
  const snapshot = await firebase.firestore().collectionGroup("products").where("productId", "==", id).get();
  if (snapshot.empty) throw Error("Product Not Found");
  return validate("tProduct")(snapshot.docs[0].data());
};

const useIsMobileSelector = () => {
  return useSelector(
    (state) => state.NavigationReducer.deviceSize === "xs" || state.NavigationReducer.deviceSize === "sm",
  );
};

const VariantItem: FC<tVariantItem> = ({
  name,
  index,
  size,
  options,
  value,
  onSelect,
  onSelectUnavailable,
  filterAvailable,
}) => {
  const handleOnSelect = (option: tOption<string>) => {
    onSelect?.(option, index);
  };

  const handleOnSelectUnavailable = (option: tOption<string>) => {
    onSelectUnavailable?.(option, index);
  };

  const handleFilterAvailable = (option: tOption<string>) => {
    return filterAvailable ? filterAvailable(option, index) : true;
  };

  return (
    <View style={[styles.mb2, { zIndex: size - index }]}>
      <Text style={styles.label}>{upperFirst(name)}</Text>
      <ComboBox
        myPlaceholder="Select an option"
        value={value}
        myOptions={options.map((option) => ({ myValue: option, myLabel: option }))}
        textInputStyle={styles.label}
        onSelect={handleOnSelect}
        onSelectUnavailable={handleOnSelectUnavailable}
        filterAvailable={handleFilterAvailable}
      />
    </View>
  );
};

const Product: FC<tModalContent & { product: tProductModal }> = ({ product, onClose, onResolve }) => {
  const variantOptions = getVariantOptions(product.variants);
  const dispatch = useAppDispatch();
  const [quantity, setQuantity] = useState(product.caseMinimum);
  const [variantValue, setVariantValue] = useState(getVariantOptionsList(product.variants[0]));
  const [isAddTester, setAddTester] = useState(false);
  const variant = useMemo(
    () => findVariantByOptionList(product.variants, variantValue),
    [product.variants, variantValue],
  );
  const rIsMobile = useIsMobileSelector();
  const imageUrl = (variant && variant.imageUrl) || product?.images?.[0]?.image_url;
  const isPublished = variant ? variant.purchasable : product.availability === "available";
  const hasStock = variant ? !!variant.inventory : !!product.inventory;
  const testerProduct = variant ? variant.tester : product.tester;
  const canAddToCart = (!(product.variants.length > 0) || variant) && isPublished && hasStock;

  const navigateToProduct = () => {
    if (navigationRef.current) {
      navigationRef.current.navigate(kRoute.P101, { brand: product.brandName, productId: product.productId });
      onClose();
    }
  };

  const handleOnSelectVariant = (option: tOption<string>, variantIndex: number) => {
    setVariantValue((prev) => [...prev.slice(0, variantIndex), option.myValue, ...prev.slice(variantIndex + 1)]);
  };

  const handleOnSelectUnavailableVariant = (option: tOption<string>, variantIndex: number) => {
    setVariantValue((prev) => prev.map((_prevOption, prevIndex) => (prevIndex === variantIndex ? option.myValue : "")));
  };

  const checkVariantAvailability = (option: tOption<string>, variantIndex: number) => {
    return isVariantAvailableByOptionValue(product.variants, variantValue, [variantIndex, option.myValue]);
  };

  const handleOnPressSubtractQuantity = () => {
    if (quantity > product.caseMinimum) {
      setQuantity((prev) => prev - product.caseMinimum);
    }
  };

  const handleOnPressAddQuantity = () => {
    setQuantity((prev) => prev + product.caseMinimum);
  };

  const handleOnChangeQuantity = (inputQuantity: number) => {
    const newQuantity = Math.round(inputQuantity / product.caseMinimum) * product.caseMinimum;
    setQuantity(bound(product.caseMinimum, variant?.inventory ?? newQuantity, newQuantity));
  };

  const handleOnPressAddTester = () => {
    setAddTester((prev) => !prev);
  };

  const handleOnPressAddToCart = async () => {
    try {
      const sku = variant ? variant.sku : product.sku;
      await dispatch(
        addProductToCart({
          brandId: product.brandUid,
          productId: product.productId,
          productSku: sku,
          quantity: quantity.toString(),
          isAddTester: isAddTester && !!testerProduct?.sku,
        }),
      );
      onResolve();
    } catch (error) {
      dispatch({
        type: "RIBBON",
        message: new Error(
          isApiError(error) && error.response ? error.response?.data.message : "An error occurred please try again.",
        ),
      });
    }
  };

  return (
    <>
      <View style={[styles.mb8, rIsMobile ? styles.alignCenter : styles.flexRow]}>
        <TouchableOpacity style={[styles.mb4, styles.mr4]} onPress={navigateToProduct}>
          <SLImage source={{ uri: imageUrl }} mobileStyle={styles.productImage} webStyle={webStyles.productImage} />
        </TouchableOpacity>
        <View style={rIsMobile ? [styles.alignCenter] : [styles.alignStart, styles.flex1]}>
          <Text style={[styles.productName, styles.mb1, rIsMobile ? styles.textAlignCenter : {}]}>{product?.name}</Text>
          <View style={[styles.flexRow, styles.alignCenter, styles.mb1]}>
            <Text style={styles.labelBold}>{`${formatMoney(
              variant?.wholesalePrice ?? product.wholesalePrice,
            )} WSP`}</Text>
            <Text style={styles.label}>{`  |  ${formatMoney(variant?.msrpPrice ?? product.msrpPrice)} MSRP`}</Text>
          </View>
          <TouchableOpacity style={styles.viewProductButton} onPress={navigateToProduct}>
            <Text style={styles.viewProductButtonLabel}>View Product Details</Text>
          </TouchableOpacity>
        </View>
      </View>
      {Object.keys(variantOptions).map((name, index) => (
        <VariantItem
          key={name}
          name={name}
          index={index}
          size={Object.keys(variantOptions).length}
          value={variantValue[index]}
          options={variantOptions[name]}
          onSelect={handleOnSelectVariant}
          onSelectUnavailable={handleOnSelectUnavailableVariant}
          filterAvailable={checkVariantAvailability}
        />
      ))}
      <View style={[styles.flexRow, styles.flexWrap, styles.alignEnd, styles.justifyBetween, styles.mt2]}>
        <View style={styles.mb2}>
          <Text style={styles.label}>Quantity</Text>
          <View style={styles.brandCartItemInfoQuantity}>
            <TouchableOpacity style={styles.cartQuantityChangeButton} onPress={handleOnPressSubtractQuantity}>
              <Text style={styles.cartQuantityChangeButtonLabel}>-</Text>
            </TouchableOpacity>
            <NumberInput
              style={styles.numberInput}
              textInputStyle={styles.cartQuantityChangeNumber}
              onInputNumber={handleOnChangeQuantity}
              onChangeNumber={setQuantity}
              value={`${quantity}`}
            />
            <TouchableOpacity style={styles.cartQuantityChangeButton} onPress={handleOnPressAddQuantity}>
              <Text style={styles.cartQuantityChangeButtonLabel}>+</Text>
            </TouchableOpacity>
          </View>
        </View>
        {testerProduct?.sku !== undefined && (
          <CheckMark2
            style={[styles.flexRow, styles.alignCenter, styles.mb2, styles.ml0]}
            textStyle={[styles.label, styles.ml2]}
            myText={`Include a Tester for ${formatMoney(testerProduct?.price ?? 0)}`}
            myState={isAddTester}
            textPlacement="right"
            onPress={handleOnPressAddTester}
          />
        )}
      </View>
      <TouchableOpacity
        disabled={!canAddToCart}
        style={[styles.addToCartButton, !canAddToCart ? styles.opacityLg : {}]}
        onPress={handleOnPressAddToCart}
      >
        <Text style={styles.addToCartButtonLabel}>{hasStock ? "ADD TO CART" : "OUT OF STOCK"}</Text>
      </TouchableOpacity>
    </>
  );
};

const ModalContent: FC<tModalContent & { product?: tProductModal }> = ({ product, ...rest }) => {
  if (!product) {
    return <ActivityIndicator style={styles.modalLoading} color={kColors.brandDarkGreen} size="small" />;
  }

  return <Product product={product} {...rest} />;
};

const mapProductModal = (product: tProduct): tProductModal => {
  return {
    name: product.name,
    brandUid: product.brandUid,
    brandName: product.brandName,
    images: product.images,
    variants: product.variants,
    availability: product.availability ?? "available",
    sku: product.sku ?? "",
    wholesalePrice: product.wholesale_price,
    msrpPrice: product.msrp_price,
    caseMinimum: product.case_minimum,
    productId: product.productId,
    inventory: product.inventory,
  };
};

export const AddProductModal: FC<tModalComponentProps<tAddProductModal>> = (props) => {
  const { onClose, onReject, onResolve, action, productUid, product } = props;
  const dispatch = useAppDispatch();
  const [productModal, setProductModal] = useState<tProductModal>();

  const handleOnClose = () => {
    onClose();
  };

  useEffect(() => {
    (async () => {
      if (action === "add") {
        try {
          const _product = await getProductById(productUid);
          setProductModal(mapProductModal(_product));
        } catch (er: unknown) {
          if (er instanceof Error) {
            dispatch({ type: "ERROR", error: er });
          }
        }
      } else if (action === "edit") {
        setProductModal(product);
      }
    })();
  }, [action, dispatch, product, productUid]);

  return (
    <PdModal onClose={handleOnClose}>
      <PdModalCloseButton onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.modal} showsHorizontalScrollIndicator={false}>
        <ModalContent onClose={handleOnClose} onReject={onReject} onResolve={onResolve} product={productModal} />
      </ScrollView>
    </PdModal>
  );
};

const styles = StyleSheet.create({
  addToCartButton: {
    ...kStyleConst.kAddToCartButtonDimensions,
    alignItems: "center",
    backgroundColor: kColors.brandDarkGreen,
    justifyContent: "center",
    marginTop: 8 * kStyleConst.kSpacing,
  },
  addToCartButtonLabel: {
    ...kFonts.ralewaySmallSemibold,
    color: kColors.white,
    letterSpacing: 1.1,
  },
  alignCenter: {
    alignItems: "center",
  },
  alignEnd: {
    alignItems: "flex-end",
  },
  alignStart: {
    alignItems: "flex-start",
  },
  brandCartItemInfoQuantity: {
    alignItems: "center",
    flexDirection: "row",
    textAlign: "center",
  },
  cartQuantityChangeButton: {
    alignItems: "center",
    justifyContent: "center",
    width: 20,
  },
  cartQuantityChangeButtonLabel: {
    color: kColors.brand500,
    fontSize: 22,
    fontWeight: "100",
    textAlign: "center",
  },
  cartQuantityChangeNumber: {
    ...kFonts.poppinsLight,
    color: kColors.brandDark,
    letterSpacing: 1,
    lineHeight: 24,
    textAlign: "center",
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  flexWrap: {
    flexWrap: "wrap",
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  label: {
    ...kFonts.poppinsLight,
    color: kColors.brandDark,
    letterSpacing: 1,
    lineHeight: 24,
  },
  labelBold: {
    ...kFonts.poppinsRegular,
    color: kColors.brandDark,
    letterSpacing: 1,
    lineHeight: 24,
  },
  mb1: {
    marginBottom: kStyleConst.kSpacing,
  },
  mb2: {
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mb8: {
    marginBottom: 8 * kStyleConst.kSpacing,
  },
  ml0: {
    marginLeft: 0,
  },
  ml2: {
    marginLeft: 2 * kStyleConst.kSpacing,
  },
  modal: {
    backgroundColor: kColors.white,
    maxWidth: 600,
    padding: 8 * kStyleConst.kSpacing,
  },
  modalLoading: {
    alignItems: "center",
    height: 250,
    justifyContent: "center",
    width: 250,
  },
  mr4: {
    marginRight: 4 * kStyleConst.kSpacing,
  },
  mt2: {
    marginTop: 2 * kStyleConst.kSpacing,
  },
  numberInput: {
    width: 45,
  },
  opacityLg: {
    opacity: 0.4,
  },
  productImage: {
    backgroundColor: kColors.white,
    borderColor: kColors.lightMed,
    borderWidth: 1,
    height: 200,
    width: 200,
  },
  productName: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontSize: 20,
    fontWeight: "400",
    letterSpacing: 1.1,
    lineHeight: 32,
  },
  textAlignCenter: {
    textAlign: "center",
  },
  viewProductButton: {
    borderBottomColor: kColors.brandDark,
    borderBottomWidth: 1.75,
  },
  viewProductButtonLabel: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
  },
});

const webStyles: { [key: string]: CSSProperties } = {
  productImage: {
    backgroundColor: kColors.white,
    borderColor: kColors.lightMed,
    borderWidth: 1,
    height: 200,
    width: 200,
  },
};
